import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  CalendarIcon,
  ListBulletIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  CheckIcon,
  XMarkIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { format, isToday, isTomorrow, startOfWeek, endOfWeek } from 'date-fns';
import { getPendingAgendas, getTodayAgendas, getUpcomingAgendas, getAgendas, createAgenda, updateAgendaStatus } from '../services/api';
import AddAgendaForm from '../components/AddAgendaForm';
import { formatTime } from '../utils/timeFormat';
import { TEAM_COLORS, FULL_DAY_TEAMS, AGENDA_TEAMS, ATTENDING_TEAMS } from '../utils/constants';

const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalAgendas: 0,
    pendingApproval: 0,
    todayAgendas: 0,
    upcomingAgendas: 0
  });
  const [recentAgendas, setRecentAgendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [pending, today, upcoming, all] = await Promise.all([
          getPendingAgendas(),
          getTodayAgendas(),
          getUpcomingAgendas(),
          getAgendas()
        ]);

        setStats({
          totalAgendas: all.length,
          pendingApproval: pending.length,
          todayAgendas: today.length,
          upcomingAgendas: upcoming.length
        });

        // Convert date strings to Date objects
        const processedAgendas = all.map(agenda => ({
          ...agenda,
          date: new Date(agenda.date)
        }));

        // Filter agendas to only those within the current week (Mulai Hari Ini - Minggu)
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const end = endOfWeek(now, { weekStartsOn: 1 });

        const thisWeekAgendas = processedAgendas.filter(agenda =>
          agenda.date >= startOfToday && agenda.date <= end
        );

        // Sort by date ascending, then by time ascending
        thisWeekAgendas.sort((a, b) => {
          if (a.date.getTime() !== b.date.getTime()) {
            return a.date - b.date;
          }
          if (a.time && b.time) {
            return a.time.localeCompare(b.time);
          }
          return 0;
        });

        setRecentAgendas(thisWeekAgendas.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user, navigate]);



  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getDateLabel = (date) => {
    if (isToday(date)) return 'Hari Ini';
    if (isTomorrow(date)) return 'Besok';
    return format(date, 'MMM dd, yyyy');
  };

  const handleApprove = async (agendaId) => {
    try {
      const updatedAgenda = await updateAgendaStatus(agendaId, 'approved');
      setRecentAgendas(prev => prev.map(agenda =>
        (agenda.id === agendaId || agenda._id === agendaId) ? { ...updatedAgenda, date: new Date(updatedAgenda.date) } : agenda
      ));
      setStats(prev => ({ ...prev, pendingApproval: Math.max(0, prev.pendingApproval - 1) }));
    } catch (error) {
      console.error('Error approving agenda:', error);
    }
  };

  const handleReject = async (agendaId) => {
    try {
      const updatedAgenda = await updateAgendaStatus(agendaId, 'rejected');
      setRecentAgendas(prev => prev.map(agenda =>
        (agenda.id === agendaId || agenda._id === agendaId) ? { ...updatedAgenda, date: new Date(updatedAgenda.date) } : agenda
      ));
      setStats(prev => ({ ...prev, pendingApproval: Math.max(0, prev.pendingApproval - 1) }));
    } catch (error) {
      console.error('Error rejecting agenda:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Stats Cards */}
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${isAdmin ? 4 : 3} gap-6 mb-8`}>
        <div className="glass-card hover:bg-white/70">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 uppercase tracking-wider truncate mb-1">
                    Total Agenda
                  </dt>
                  <dd className="text-3xl font-light text-slate-800 tracking-tight">
                    {stats.totalAgendas}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card hover:bg-white/70">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 uppercase tracking-wider truncate mb-1">
                    Agenda Hari Ini
                  </dt>
                  <dd className="text-3xl font-light text-slate-800 tracking-tight">
                    {stats.todayAgendas}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card hover:bg-white/70">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 uppercase tracking-wider truncate mb-1">
                    Agenda Datang
                  </dt>
                  <dd className="text-3xl font-light text-slate-800 tracking-tight">
                    {stats.upcomingAgendas}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="glass-card hover:bg-white/70">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 uppercase tracking-wider truncate mb-1">
                      Agenda Pending
                    </dt>
                    <dd className="text-3xl font-light text-slate-800 tracking-tight">
                      {stats.pendingApproval}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <Link
          to="/calendar"
          className="glass-card flex items-center p-6 group hover:scale-[1.02] hover:bg-white/80"
        >
          <div className="p-6">
            <div className="flex items-center w-full">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center border border-primary-100 group-hover:scale-110 transition-transform duration-300">
                <CalendarIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-slate-800">Lihat Kalender</h3>
                <p className="text-sm text-slate-500">Seluruh agenda pada kalender</p>
              </div>
            </div>
          </div>
        </Link>

        <Link
          to="/agenda-list"
          className="glass-card flex items-center p-6 group hover:scale-[1.02] hover:bg-white/80"
        >
          <div className="p-6">
            <div className="flex items-center w-full">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center border border-primary-100 group-hover:scale-110 transition-transform duration-300">
                <ListBulletIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-slate-800">List Agenda</h3>
                <p className="text-sm text-slate-500">Tabel seluruh agenda lengkap</p>
              </div>
            </div>
          </div>
        </Link>

        <button
          onClick={() => {
            if (user) {
              setShowAddModal(true);
            } else {
              navigate('/login');
            }
          }}
          className="relative overflow-hidden rounded-xl bg-slate-800 p-6 flex items-center w-full text-left group hover:scale-[1.02] hover:bg-slate-700 transition-all duration-300 shadow-md shadow-slate-800/20"
        >
          {/* subtle glow effect */}
          <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 rounded-full bg-primary-500/20 blur-2xl group-hover:bg-primary-500/30 transition-all duration-500"></div>
          <div className="p-6">
            <div className="flex items-center w-full relative z-10">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center backdrop-blur-sm border border-white/20 group-hover:scale-110 transition-transform duration-300">
                <PlusIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-white tracking-wide">Tambah Agenda</h3>
                <p className="text-sm text-slate-300 font-light">Tambahkan jadwal baru</p>
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Recent Agendas */}
      <div className="glass-panel">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-slate-800 mb-6 tracking-tight">
            Agenda Minggu Ini
          </h3>
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-slate-200/50">
              {recentAgendas.map((agenda) => {
                const teamColor = TEAM_COLORS[agenda.team] || TEAM_COLORS.default;
                const isFullDay = FULL_DAY_TEAMS.includes(agenda.team);

                return (
                  <li key={agenda._id} className={`py-4 group transition-colors duration-200 -mx-4 px-4 rounded-xl ${isFullDay ? 'bg-amber-50/40 hover:bg-amber-50/70 border-amber-100/50' : 'hover:bg-white/40'}`}>
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center border shadow-sm ${teamColor}`}>
                          <CalendarIcon className="h-5 w-5 opacity-80" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-medium text-slate-800 truncate">
                          {agenda.title}
                        </p>
                        <p className="text-sm text-slate-500 truncate mt-0.5">
                          {agenda.description}
                        </p>
                        <div className="flex items-center mt-2 space-x-2">
                          <span className="text-xs text-slate-500 font-medium">
                            {getDateLabel(agenda.date)} pada {formatTime(agenda.time)}
                          </span>
                          <span className="text-xs text-slate-300">•</span>
                          <span className="text-xs text-slate-500">
                            Diusulkan oleh {AGENDA_TEAMS[agenda.team] || agenda.createdBy}
                          </span>
                          <span className="text-xs text-slate-300">•</span>
                          <span className="text-xs text-slate-500">
                            Dihadiri oleh {ATTENDING_TEAMS[agenda.attendingTeam] || 'Tidak diketahui'}
                          </span>
                          {agenda.location && (
                            <>
                              <span className="text-xs text-slate-300">•</span>
                              <span className="text-xs text-slate-500 flex items-center">
                                <MapPinIcon className="h-3 w-3 mr-1" />
                                {agenda.location}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-4 flex flex-col items-end space-y-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium tracking-wide uppercase ${getStatusColor(agenda.status)}`}>
                          {agenda.status}
                        </span>
                        {/* Admin Actions */}
                        {isAdmin && agenda.status === 'pending' && (
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => { e.preventDefault(); handleApprove(agenda.id || agenda._id); }}
                              className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-green-500 hover:bg-green-600 focus:outline-none transition-all duration-200"
                              title="Setujui"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => { e.preventDefault(); handleReject(agenda.id || agenda._id); }}
                              className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-red-500 hover:bg-red-600 focus:outline-none transition-all duration-200"
                              title="Tolak"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="mt-6">
            <Link
              to="/agenda-list"
              className="w-full flex justify-center items-center px-4 py-2 border border-slate-200/60 shadow-sm text-sm font-medium rounded-xl text-slate-700 bg-white/50 backdrop-blur-sm hover:bg-white/80 transition-all duration-300"
            >
              Lihat seluruh agenda
            </Link>
          </div>
        </div>
      </div>

      {/* Add Agenda Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-lg glass-panel flex flex-col max-h-[90vh]">
            <div className="p-8 overflow-y-auto no-scrollbar">
              <h3 className="text-2xl font-light text-slate-800 mb-8 tracking-tight shrink-0">Tambah Agenda Baru</h3>
              <AddAgendaForm
                onSubmit={async (formData) => {
                  try {
                    // Call API to create agenda
                    await createAgenda(formData);
                    setShowAddModal(false);
                    // Refresh agendas
                    const [pending, today, upcoming, all] = await Promise.all([
                      getPendingAgendas(),
                      getTodayAgendas(),
                      getUpcomingAgendas(),
                      getAgendas()
                    ]);
                    setStats({
                      totalAgendas: all.length,
                      pendingApproval: pending.length,
                      todayAgendas: today.length,
                      upcomingAgendas: upcoming.length
                    });
                    // Convert date strings to Date objects
                    const processedAgendas = all.map(agenda => ({
                      ...agenda,
                      date: new Date(agenda.date)
                    }));

                    // Filter agendas to only those within the next 7 days including today
                    const now = new Date();
                    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const end = new Date(start);
                    end.setDate(end.getDate() + 7);

                    const thisWeekAgendas = processedAgendas.filter(agenda =>
                      agenda.date >= start && agenda.date <= end
                    );

                    // Sort by date descending
                    thisWeekAgendas.sort((a, b) => b.date - a.date);

                    setRecentAgendas(thisWeekAgendas.slice(0, 5));
                  } catch (error) {
                    console.error('Failed to add agenda:', error);
                  }
                }}
                onCancel={() => setShowAddModal(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>

  );
};

export default Dashboard;
